<template>
  <div class="overlay" @click="$emit('close')">
    <div class="dlg" @click.stop>
      <!-- Cabecera -->
      <div class="head">
        <div class="head-info">
          <div class="folio-linea">
            <span class="folio">{{ plan.folio }}</span>
            <PortalBadge
              :text="plan.estatusDesc || estatusLabel(plan.estatus)"
              :color="estatusColor(plan.estatus)"
            />
            <span class="sem" :style="{ color: sem.color }">{{ sem.label }}</span>
          </div>
          <h3 class="titulo">{{ plan.titulo }}</h3>
          <div class="sub">
            {{ plan.claim ? plan.claim.cliente : "" }}
            <span v-if="plan.claim"> · {{ plan.claim.folio }}</span>
          </div>
        </div>
        <button class="icon-btn" @click="$emit('close')">
          <PortalIcon name="X" :size="20" />
        </button>
      </div>

      <!-- Pestañas -->
      <div class="tabs">
        <button
          v-for="t in tabs"
          :key="t.id"
          class="tab-btn"
          :class="{ on: vista === t.id }"
          @click="vista = t.id"
        >
          {{ t.label }}
          <span v-if="t.n" class="tab-n">{{ t.n }}</span>
        </button>
      </div>

      <!-- Contenido -->
      <div class="body">
        <TabResumen
          v-if="vista === 'resumen'"
          :plan="plan"
          :editable="acciones.causaRaiz"
          :guardando="busy === 'causa'"
          @guardar-causa="onCausaRaiz"
        />

        <div v-else-if="vista === 'hallazgo'" class="hallazgo">
          <div class="datos">
            <div class="dato">
              <span class="lbl">Folio</span>
              <span class="val">{{ plan.claim.folio || "—" }}</span>
            </div>
            <div class="dato">
              <span class="lbl">Cliente</span>
              <span class="val">{{ plan.claim.cliente || "—" }}</span>
            </div>
            <div class="dato">
              <span class="lbl">Canal</span>
              <span class="val">{{ plan.claim.canal || "—" }}</span>
            </div>
            <div class="dato">
              <span class="lbl">Solicitante</span>
              <span class="val">{{ plan.claim.solicitante || "—" }}</span>
            </div>
            <div class="dato">
              <span class="lbl">Fecha inicial</span>
              <span class="val">{{ fmtFecha(plan.claim.fechaInicial) }}</span>
            </div>
          </div>
          <div class="bloque">
            <div class="bloque-titulo">Asunto</div>
            <p class="texto">{{ plan.claim.asunto }}</p>
          </div>
        </div>

        <TabActividades
          v-else-if="vista === 'actividades'"
          :plan="plan"
          :editable="acciones.agregarTarea"
          :agregando="busy === 'tarea'"
          :guardando="tareaBusy"
          @agregar-tarea="onAgregarTarea"
          @avance-tarea="onAvanceTarea"
        />

        <TabEvidencias
          v-else-if="vista === 'evidencias'"
          :plan="plan"
          :editable="acciones.agregarEvidencia"
          :puede-revisar="acciones.revisarEvidencia"
          :agregando="busy === 'evidencia'"
          @agregar-evidencia="onAgregarEvidencia"
          @aprobar="onAprobarEvidencia"
          @rechazar="(e) => (prompt = { tipo: 'rechazoEvidencia', evidencia: e })"
        />

        <TabComentarios
          v-else-if="vista === 'comentarios'"
          :plan="plan"
          :enviando="busy === 'comentario'"
          @comentar="onComentar"
        />

        <TabHistorial v-else-if="vista === 'historial'" :plan="plan" />

        <TabValidacion
          v-else-if="vista === 'validacion'"
          :plan="plan"
          :puede-aprobar="puedeAprobarFecha"
          @aprobar-fecha="(c) => onResolverFecha(c, true)"
          @rechazar-fecha="(c) => onResolverFecha(c, false)"
        />
      </div>

      <!-- Acciones de flujo -->
      <div class="pie">
        <p v-if="error" class="pie-error">{{ error }}</p>
        <div class="pie-btns">
          <button v-if="acciones.asignar" class="act primary" @click="asignar = true">
            Asignar al gerente del área
          </button>
          <button v-if="acciones.aceptar" class="act ok" @click="onAceptar">
            Aceptar asignación
          </button>
          <button
            v-if="acciones.rechazar"
            class="act danger"
            @click="prompt = { tipo: 'rechazo' }"
          >
            Rechazar
          </button>
          <button
            v-if="acciones.cambiarFecha"
            class="act ghost"
            @click="cambioFecha = true"
          >
            Solicitar cambio de fecha
          </button>
          <button
            v-if="mostrarEnviar"
            class="act primary"
            :disabled="!acciones.enviarValidacion"
            :title="tituloEnviar"
            @click="onEnviarValidacion"
          >
            Enviar a validación
          </button>
          <button
            v-if="acciones.observaciones"
            class="act warn"
            @click="prompt = { tipo: 'observaciones' }"
          >
            Solicitar correcciones
          </button>
          <button
            v-if="mostrarCerrar"
            class="act ok"
            :disabled="!acciones.cerrar"
            :title="tituloCerrar"
            @click="cerrar = true"
          >
            Validar y cerrar
          </button>
          <button
            v-if="acciones.reabrir"
            class="act danger"
            @click="prompt = { tipo: 'reapertura' }"
          >
            Reabrir
          </button>
        </div>
      </div>

      <!-- Diálogos -->
      <PromptDialog
        v-if="prompt"
        :title="promptCfg.title"
        :hint="promptCfg.hint"
        :placeholder="promptCfg.placeholder"
        :confirm-text="promptCfg.confirmText"
        :color="promptCfg.color"
        :min="promptCfg.min"
        :busy="busy === 'prompt'"
        :error="promptError"
        @confirm="onPromptConfirm"
        @close="cerrarPrompt"
      />

      <CerrarPlanModal
        v-if="cerrar"
        :plan="plan"
        :busy="busy === 'cerrar'"
        :error="error"
        @confirm="onCerrar"
        @close="cerrar = false"
      />

      <AsignarModal
        v-if="asignar"
        :plan="plan"
        :busy="busy === 'asignar'"
        :error="error"
        @confirm="onAsignar"
        @close="asignar = false"
      />

      <CambioFechaModal
        v-if="cambioFecha"
        :plan="plan"
        :busy="busy === 'fecha'"
        :error="error"
        @confirm="onCambioFecha"
        @close="cambioFecha = false"
      />
    </div>
  </div>
</template>

<script>
import PortalIcon from "./PortalIcon.vue";
import PortalBadge from "./PortalBadge.vue";
import TabResumen from "./plan/TabResumen.vue";
import TabActividades from "./plan/TabActividades.vue";
import TabEvidencias from "./plan/TabEvidencias.vue";
import TabComentarios from "./plan/TabComentarios.vue";
import TabHistorial from "./plan/TabHistorial.vue";
import TabValidacion from "./plan/TabValidacion.vue";
import AsignarModal from "./plan/AsignarModal.vue";
import PromptDialog from "./plan/PromptDialog.vue";
import CerrarPlanModal from "./plan/CerrarPlanModal.vue";
import CambioFechaModal from "./plan/CambioFechaModal.vue";
import {
  semaforo, fmtFecha, estatusLabel, estatusColor,
  accionesDisponibles, puedeCerrar, puedeEnviarAValidacion, esCX,
} from "./cx.rules";
import cxService from "../../../services/cx.service";

// Configuración de cada diálogo de texto. Los mínimos replican los que valida
// PKG_CRM_CX_PLAN, para que el botón no se habilite si el backend va a rechazar.
const PROMPTS = {
  rechazo: {
    title: "Rechazar la asignación",
    hint: "El rechazo requiere justificación (RN-04). CX tendrá que reasignar el plan.",
    placeholder: "Por qué no corresponde a tu área o no puedes tomarlo",
    confirmText: "Rechazar", color: "#dc2626", min: 10,
  },
  reapertura: {
    title: "Reabrir el plan",
    hint: "La reapertura conserva todo el historial y exige motivo (RN-10).",
    placeholder: "Por qué la solución no fue efectiva",
    confirmText: "Reabrir", color: "#dc2626", min: 10,
  },
  observaciones: {
    title: "Solicitar correcciones",
    hint: "El plan regresa a 'Con observaciones' y se notifica al responsable.",
    placeholder: "Qué debe corregirse antes de validar",
    confirmText: "Enviar observaciones", color: "#d97706", min: 5,
  },
  rechazoEvidencia: {
    title: "Rechazar la evidencia",
    hint: "El comentario es obligatorio y se conserva en el historial (RN-15).",
    placeholder: "Por qué la evidencia no es suficiente",
    confirmText: "Rechazar evidencia", color: "#dc2626", min: 5,
  },
  rechazoFecha: {
    title: "Rechazar el cambio de fecha",
    hint: "La fecha compromiso vigente no cambiará.",
    placeholder: "Por qué no se aprueba la nueva fecha",
    confirmText: "Rechazar", color: "#dc2626", min: 5,
  },
};

export default {
  name: "PlanDetalle",
  components: {
    PortalIcon, PortalBadge,
    TabResumen, TabActividades, TabEvidencias, TabComentarios,
    TabHistorial, TabValidacion,
    AsignarModal, PromptDialog, CerrarPlanModal, CambioFechaModal,
  },
  props: {
    plan: { type: Object, required: true },
    sesion: { type: Object, default: () => ({}) },
  },
  emits: ["close", "actualizado"],
  data() {
    return {
      vista: "resumen",
      busy: "",
      error: "",
      promptError: "",
      prompt: null,
      cerrar: false,
      cambioFecha: false,
      asignar: false,
      tareaBusy: null,
    };
  },
  computed: {
    sem() {
      return semaforo(this.plan);
    },
    acciones() {
      return accionesDisponibles(this.plan, this.sesion);
    },
    tabs() {
      return [
        { id: "resumen", label: "Resumen" },
        { id: "hallazgo", label: "Hallazgo" },
        { id: "actividades", label: "Actividades", n: this.plan.tareas.length },
        { id: "evidencias", label: "Evidencias", n: this.plan.evidencias.length },
        { id: "comentarios", label: "Comentarios", n: this.plan.comentarios.length },
        { id: "historial", label: "Historial", n: this.plan.historial.length },
        { id: "validacion", label: "Validación" },
      ];
    },
    // El botón se muestra aunque esté deshabilitado: si desaparece, el usuario
    // no sabe que existe ni qué le falta para llegar ahí.
    mostrarEnviar() {
      const esResp = this.plan.idResponsable === this.sesion.idColaborador;
      return (
        (esResp || this.sesion.rol === "admin") &&
        ["en_implementacion", "plan_definido", "con_observaciones", "vencido"].indexOf(
          this.plan.estatus
        ) !== -1
      );
    },
    mostrarCerrar() {
      return (
        esCX(this.sesion.rol) &&
        ["en_validacion", "vencido"].indexOf(this.plan.estatus) !== -1
      );
    },
    tituloEnviar() {
      const r = puedeEnviarAValidacion(this.plan);
      return r.ok ? "Enviar a validación de CX" : r.faltan.join(". ");
    },
    tituloCerrar() {
      const r = puedeCerrar(this.plan);
      return r.ok ? "Validar y cerrar el plan" : r.faltan.join(". ");
    },
    puedeAprobarFecha() {
      return ["gerente", "cx", "admin", "direccion"].indexOf(this.sesion.rol) !== -1;
    },
    promptCfg() {
      return (this.prompt && PROMPTS[this.prompt.tipo]) || {};
    },
  },
  methods: {
    fmtFecha,
    estatusLabel,
    estatusColor,

    // Envoltura común: marca ocupado, traduce el error del backend y pide al
    // padre que recargue el plan. Los mensajes de ERROR_ID 400 vienen del
    // paquete PL/SQL y ya están redactados para el usuario.
    async ejecutar(clave, fn) {
      this.busy = clave;
      this.error = "";
      this.promptError = "";
      try {
        await fn();
        this.$emit("actualizado");
        return true;
      } catch (e) {
        const msg = e.message || "Ocurrió un error.";
        this.error = msg;
        this.promptError = msg;
        return false;
      } finally {
        this.busy = "";
      }
    },

    async onAsignar() {
      const ok = await this.ejecutar("asignar", () =>
        cxService.asignarResponsable(this.plan.idPlan, this.plan.idGerenteArea)
      );
      if (ok) this.asignar = false;
    },
    async onAceptar() {
      await this.ejecutar("aceptar", () => cxService.aceptarPlan(this.plan.idPlan));
    },
    async onCausaRaiz(f) {
      await this.ejecutar("causa", () => cxService.guardarCausaRaiz(this.plan.idPlan, f));
    },
    async onAgregarTarea(f) {
      await this.ejecutar("tarea", () => cxService.agregarTarea(this.plan.idPlan, f));
    },
    async onAvanceTarea(idTarea, avance) {
      this.tareaBusy = idTarea;
      await this.ejecutar("", () =>
        cxService.actualizarTarea(this.plan.idPlan, idTarea, avance)
      );
      this.tareaBusy = null;
    },
    async onAgregarEvidencia(f) {
      await this.ejecutar("evidencia", () =>
        cxService.agregarEvidencia(this.plan.idPlan, f)
      );
    },
    async onAprobarEvidencia(e) {
      await this.ejecutar("", () =>
        cxService.revisarEvidencia(this.plan.idPlan, e.idEvidencia, true, null)
      );
    },
    async onComentar(texto) {
      await this.ejecutar("comentario", () =>
        cxService.agregarComentario(this.plan.idPlan, texto)
      );
    },
    async onEnviarValidacion() {
      await this.ejecutar("", () => cxService.enviarAValidacion(this.plan.idPlan));
    },
    async onCerrar(f) {
      const ok = await this.ejecutar("cerrar", () =>
        cxService.cerrarPlan(this.plan.idPlan, f)
      );
      if (ok) this.cerrar = false;
    },
    async onCambioFecha(f) {
      const ok = await this.ejecutar("fecha", () =>
        cxService.solicitarCambioFecha(this.plan.idPlan, f)
      );
      if (ok) this.cambioFecha = false;
    },
    async onResolverFecha(c, aprueba) {
      if (aprueba) {
        await this.ejecutar("", () =>
          cxService.resolverCambioFecha(c.idCambioFecha, true, null)
        );
      } else {
        this.prompt = { tipo: "rechazoFecha", solicitud: c };
      }
    },

    cerrarPrompt() {
      this.prompt = null;
      this.promptError = "";
    },
    async onPromptConfirm(texto) {
      const p = this.prompt;
      let ok = false;
      if (p.tipo === "rechazo") {
        ok = await this.ejecutar("prompt", () =>
          cxService.rechazarPlan(this.plan.idPlan, texto)
        );
      } else if (p.tipo === "reapertura") {
        ok = await this.ejecutar("prompt", () =>
          cxService.reabrirPlan(this.plan.idPlan, texto)
        );
      } else if (p.tipo === "observaciones") {
        ok = await this.ejecutar("prompt", () =>
          cxService.solicitarCorrecciones(this.plan.idPlan, texto)
        );
      } else if (p.tipo === "rechazoEvidencia") {
        ok = await this.ejecutar("prompt", () =>
          cxService.revisarEvidencia(this.plan.idPlan, p.evidencia.idEvidencia, false, texto)
        );
      } else if (p.tipo === "rechazoFecha") {
        ok = await this.ejecutar("prompt", () =>
          cxService.resolverCambioFecha(p.solicitud.idCambioFecha, false, texto)
        );
      }
      if (ok) this.cerrarPrompt();
    },
  },
};
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.45);
}
.dlg {
  width: 100%;
  max-width: 900px;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--pt-card);
  color: var(--pt-text);
  border-radius: 16px;
  overflow: hidden;
}
.head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 14px;
}
.folio-linea {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.folio {
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--pt-dim);
}
.sem {
  font-size: 12px;
  font-weight: 600;
}
.titulo {
  font-size: 18px;
  font-weight: 700;
  margin: 6px 0 2px;
}
.sub {
  font-size: 13px;
  color: var(--pt-dim);
}
.icon-btn {
  background: none;
  border: 0;
  cursor: pointer;
  color: var(--pt-muted);
}
.tabs {
  display: flex;
  gap: 4px;
  padding: 0 12px;
  overflow-x: auto;
  border-bottom: 1px solid var(--pt-border);
}
.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  background: none;
  border: 0;
  border-bottom: 2px solid transparent;
  color: var(--pt-muted);
  cursor: pointer;
  white-space: nowrap;
  font-family: inherit;
}
.tab-btn.on {
  color: #003ca5;
  border-bottom-color: #003ca5;
}
.tab-n {
  font-size: 11px;
  font-weight: 700;
  background: var(--pt-track);
  border-radius: 999px;
  padding: 1px 6px;
}
.body {
  flex: 1;
  overflow-y: auto;
  padding: 18px 20px;
}
.pie {
  border-top: 1px solid var(--pt-border);
  padding: 12px 20px;
  background: var(--pt-elev);
}
.pie-error {
  margin: 0 0 8px;
  font-size: 13px;
  color: #b91c1c;
}
.pie-btns {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.act {
  padding: 9px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  border: 0;
  cursor: pointer;
  color: #fff;
  font-family: inherit;
}
.act:disabled {
  background: #cbd5e1 !important;
  cursor: not-allowed;
}
.act.primary {
  background: #003ca5;
}
.act.ok {
  background: #16a34a;
}
.act.danger {
  background: #dc2626;
}
.act.warn {
  background: #d97706;
}
.act.ghost {
  background: var(--pt-track);
  color: var(--pt-dim);
}

/* Pestaña Hallazgo (solo lectura, va en línea por su tamaño) */
.hallazgo {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.datos {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  background: var(--pt-elev);
  border-radius: 12px;
  padding: 14px;
}
.dato {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.lbl {
  font-size: 11px;
  font-weight: 600;
  color: var(--pt-muted);
  text-transform: uppercase;
}
.val {
  font-size: 14px;
}
.bloque {
  border: 1px solid var(--pt-border);
  border-radius: 12px;
  padding: 14px;
}
.bloque-titulo {
  font-size: 12px;
  font-weight: 700;
  color: var(--pt-dim);
  text-transform: uppercase;
  margin-bottom: 10px;
}
.texto {
  margin: 0;
  font-size: 14px;
  color: var(--pt-dim);
  white-space: pre-wrap;
}
</style>
